import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, DestroyRef, inject, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { debounceTime } from 'rxjs/operators';
import { firstValueFrom, Subscription } from 'rxjs';

import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

import { ComplianceApiService } from 'src/shared/services/compliance-api.service';
import { ComplianceItem } from 'src/models/compliance.model';
import { ComplianceItemDetailPage } from 'src/pages/compliance/compliance-item-detail/compliance-item-detail';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

@Component({
  templateUrl: 'completed-compliance.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CompletedCompliancePage implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private complianceApi = inject(ComplianceApiService);
  private destroyRef = inject(DestroyRef);
  private client = inject(Client);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private fb = inject(FormBuilder);
  private util = inject(Util);
  readonly windowService = inject(WindowService);

  form: UntypedFormGroup;
  headers: string[] = ['Name', 'What To Do', 'Frequency', 'Next Due', 'Status', 'last-column'];
  allRows: any[] = [];
  shownRows: any[] = [];
  loaded = false;
  errorMessage: string;
  addressItems: any[] = [];
  selectedAddressId: number | null = null;
  selectedPropertyValue: string | number = null;
  complianceItems: ComplianceItem[] = [];
  private panelClosedSubscription: Subscription;

  constructor() {
    this.form = this.fb.group({
      search: [''],
    });
    this.form.get('search')?.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => this.updateSearch(value));
  }

  async ngOnInit() {
    try {
      const paramAddressId = this.navCtrl.getParam('selectedAddressId');
      if (paramAddressId) {
        this.selectedAddressId = Number(paramAddressId);
        this.selectedPropertyValue = this.selectedAddressId;
      }
      await this.loadPage();
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.util.showError(this.errorMessage, 10000);
    }
  }

  async loadPage() {
    const addressResponse = await this.client.getAddresses();
    this.addressItems = this.client.parseAddressList(addressResponse, true);
    this.addressItems.pop();

    if (!this.selectedPropertyValue && this.addressItems.length) {
      const savedId = localStorage.getItem('complianceAddressId') || localStorage.getItem('dashboardAddressId');
      const savedNum = savedId ? Number(savedId) : null;
      const match = savedNum && this.addressItems.find(a => a.value === savedNum);
      this.selectedPropertyValue = match ? savedNum : this.addressItems[0].value;
    }

    if (this.selectedPropertyValue) {
      this.selectedAddressId = Number(this.selectedPropertyValue);
      await this.loadCompletedItems();
    } else {
      this.selectedAddressId = null;
      this.complianceItems = [];
    }

    const rows = this.buildRows(this.complianceItems);
    this.allRows = rows;
    this.shownRows = rows;
    this.loaded = true;
    this.cdr.markForCheck();
  }

  private async loadCompletedItems() {
    if (!this.selectedAddressId) { return; }
    try {
      const allItems = await firstValueFrom(this.complianceApi.getItems(this.selectedAddressId));
      this.complianceItems = allItems.filter(item => item.status === 'completed');
    } catch {
      this.complianceItems = [];
    }
  }

  async onDesktopPropertyChange() {
    this.loaded = false;
    if (this.selectedPropertyValue) {
      localStorage.setItem('complianceAddressId', String(this.selectedPropertyValue));
    }
    this.cdr.markForCheck();
    try {
      await this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message;
      this.loaded = true;
      this.cdr.markForCheck();
    }
  }

  buildRows(items: ComplianceItem[]): any[] {
    return items.map(item => {
      const displayName = item.name || item.what_to_do || 'Compliance item';
      const nameValue = item.is_tax && item.name ? `${item.name} (Tax)` : displayName;
      return {
        'Name': { value: nameValue },
        'What To Do': { value: item.what_to_do || '' },
        'Frequency': { value: this.formatFrequency(item.frequency) },
        'Next Due': { value: item.next_due_at ? new Date(item.next_due_at).toLocaleDateString() : '' },
        'Status': { value: 'Completed' },
        'last-column': { value: '', hasChevron: true },
        id: item.id,
      };
    });
  }

  formatFrequency(frequency: string | null | undefined): string {
    if (!frequency) { return ''; }
    return frequency.charAt(0).toUpperCase() + frequency.slice(1);
  }

  updateSearch(search: string) {
    if (!search || search.length < 2) {
      this.shownRows = [...this.allRows];
      this.cdr.markForCheck();
      return;
    }
    const term = search.toLowerCase();
    this.shownRows = this.allRows.filter(row =>
      String(row['Name'].value || '').toLowerCase().includes(term) ||
      String(row['What To Do'].value || '').toLowerCase().includes(term),
    );
    this.cdr.markForCheck();
  }

  sortChanged(sort: any) {
    if (!sort.active || !sort.direction) { return; }
    this.shownRows = [...this.shownRows].sort((a, b) => {
      const valA = a[sort.active]?.value || '';
      const valB = b[sort.active]?.value || '';
      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      if (strA < strB) { return sort.direction === 'asc' ? -1 : 1; }
      if (strA > strB) { return sort.direction === 'asc' ? 1 : -1; }
      return 0;
    });
  }

  goToItemDetail(itemId: number) {
    const params = { complianceItemId: itemId };
    this.goToPage('compliance-item-detail', params, ComplianceItemDetailPage);
  }

  async goToPage(url: string, params: any, component: any) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(async () => {
        await this.loadPage();
      });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }

  onTableColumnClick(event: { column: string; element: any }) {
    if (event.element?.id) {
      this.goToItemDetail(event.element.id);
    }
  }

  goBack() {
    this.navCtrl.navigateBack('compliance');
  }
}
